// Microphone-only recording for #770. This is intentionally separate from
// screen-recording-client: it never calls getDisplayMedia and therefore never
// exposes a screen picker for an audio-only choice.

import {
  DEFAULT_MAX_DURATION_MS,
  type MediaRecorderLike,
  type RecordingState,
  type StateChangeDetail,
} from './screen-recording-client.ts';

export type AudioRecordingCodec = 'audio/webm; codecs=opus' | 'audio/mp4';

export interface AudioCapabilityCheckResult {
  supported: boolean;
  reason?: 'no_get_user_media' | 'no_media_recorder' | 'no_supported_codec' | 'microphone_blocked';
  codec?: AudioRecordingCodec;
}

export interface AudioRecordingResult {
  blob: Blob;
  filename: string;
  contentType: 'audio/webm' | 'audio/mp4';
  durationMs: number;
  autoStopped: boolean;
}

export interface AudioRecordingController {
  readonly state: RecordingState;
  readonly stopped: boolean;
  stop(): Promise<AudioRecordingResult>;
  cancel(): void;
  onStateChange(cb: (state: RecordingState, detail?: StateChangeDetail) => void): () => void;
}

export interface StartAudioRecordingOptions {
  maxDurationMs?: number;
  /** Local observer of the existing microphone stream; never requests another stream. */
  onStream?: (stream: MediaStream) => void;
  /** Stops and discards capture if its accumulated source exceeds this bound. */
  maxBytes?: number;
  getUserMedia?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  createRecorder?: (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorderLike;
  isTypeSupported?: (type: string) => boolean;
  setTimeout?: (cb: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
  now?: () => number;
}

const PREFERRED_CODECS: readonly AudioRecordingCodec[] = ['audio/webm; codecs=opus', 'audio/mp4'];

function microphonePolicyAllows(): boolean {
  try {
    const policy = (
      document as Document & { featurePolicy?: { allowsFeature?: (name: string) => boolean } }
    ).featurePolicy;
    return typeof policy?.allowsFeature === 'function' ? policy.allowsFeature('microphone') : true;
  } catch {
    return true;
  }
}

export function checkAudioRecordingSupport(
  options: { isTypeSupported?: (type: string) => boolean; ignorePermissionsPolicy?: boolean } = {},
): AudioCapabilityCheckResult {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { supported: false, reason: 'no_get_user_media' };
  }
  if (!options.ignorePermissionsPolicy && !microphonePolicyAllows()) {
    return { supported: false, reason: 'microphone_blocked' };
  }
  if (typeof MediaRecorder === 'undefined')
    return { supported: false, reason: 'no_media_recorder' };
  const supports = options.isTypeSupported ?? MediaRecorder.isTypeSupported.bind(MediaRecorder);
  const codec = PREFERRED_CODECS.find((type) => supports(type));
  return codec ? { supported: true, codec } : { supported: false, reason: 'no_supported_codec' };
}

export async function startAudioRecording(
  options: StartAudioRecordingOptions = {},
): Promise<AudioRecordingController> {
  const supports = options.isTypeSupported ?? MediaRecorder.isTypeSupported.bind(MediaRecorder);
  const capability = checkAudioRecordingSupport({ isTypeSupported: supports });
  if (!capability.supported || !capability.codec) {
    throw new Error(`audio_recording_unsupported:${capability.reason ?? 'unknown'}`);
  }
  const codec = capability.codec;
  const getUserMedia =
    options.getUserMedia ?? ((constraints) => navigator.mediaDevices.getUserMedia(constraints));
  const createRecorder =
    options.createRecorder ??
    ((stream, opts) => new MediaRecorder(stream, opts) as unknown as MediaRecorderLike);
  const now = options.now ?? (() => Date.now());
  const setTimeoutFn = options.setTimeout ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimeoutFn = options.clearTimeout ?? ((handle) => clearTimeout(handle as number));
  const stream = await getUserMedia({ audio: true });
  let recorder: MediaRecorderLike;
  try {
    recorder = createRecorder(stream, { mimeType: codec, audioBitsPerSecond: 128_000 });
  } catch (error) {
    stream.getTracks().forEach((track) => {
      track.stop();
    });
    throw error;
  }
  const startedAt = now();
  const chunks: Blob[] = [];
  let capturedBytes = 0;
  const listeners = new Set<(state: RecordingState, detail?: StateChangeDetail) => void>();
  let state: RecordingState = 'recording';
  let autoStopped = false;
  let suppressResult = false;
  let timeout: unknown;
  let result: AudioRecordingResult | null = null;
  let failure: Error | null = null;
  let stopping: Promise<AudioRecordingResult> | null = null;
  let resolveStop: ((value: AudioRecordingResult) => void) | null = null;
  let rejectStop: ((reason: Error) => void) | null = null;
  const emit = (next: RecordingState, detail?: StateChangeDetail) => {
    state = next;
    listeners.forEach((listener) => {
      try {
        listener(next, detail);
      } catch {
        /* observers cannot break cleanup */
      }
    });
  };
  const stopTracks = () =>
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {
        /* already stopped */
      }
    });
  recorder.ondataavailable = (event) => {
    if (suppressResult || failure) return;
    capturedBytes += event.data.size;
    if (options.maxBytes !== undefined && capturedBytes > options.maxBytes) {
      failure = new Error('audio_recording_too_large');
      chunks.length = 0;
      if (timeout !== undefined) clearTimeoutFn(timeout);
      stopTracks();
      emit('error', { error: failure.message });
      rejectStop?.(failure);
      if (recorder.state === 'recording') recorder.stop();
      return;
    }
    if (event.data.size > 0) chunks.push(event.data);
  };
  recorder.onstop = () => {
    if (timeout !== undefined) clearTimeoutFn(timeout);
    stopTracks();
    if (failure) return;
    const durationMs = now() - startedAt;
    emit('stopped', { durationMs, autoStopped });
    if (suppressResult || failure) return;
    result = {
      blob: new Blob(chunks, { type: codec }),
      filename: `audio-${new Date(startedAt).toISOString().replace(/[:.]/g, '-')}.${codec.startsWith('audio/webm') ? 'webm' : 'mp4'}`,
      contentType: codec.startsWith('audio/webm') ? 'audio/webm' : 'audio/mp4',
      durationMs,
      autoStopped,
    };
    resolveStop?.(result);
    resolveStop = null;
  };
  recorder.onerror = (event) => {
    if (timeout !== undefined) clearTimeoutFn(timeout);
    stopTracks();
    failure = event.error ?? new Error('audio_recorder_error');
    emit('error', { error: failure.message });
    rejectStop?.(failure);
    rejectStop = null;
  };
  stream.getAudioTracks().forEach((track) => {
    track.addEventListener('ended', () => {
      if (recorder.state === 'recording') recorder.stop();
    });
  });
  try {
    recorder.start(1000);
  } catch (error) {
    stopTracks();
    throw error;
  }
  try {
    options.onStream?.(stream);
  } catch {
    /* Optional observers cannot break recording cleanup. */
  }
  emit('recording');
  timeout = setTimeoutFn(() => {
    if (recorder.state === 'recording') {
      autoStopped = true;
      try {
        recorder.stop();
      } finally {
        stopTracks();
      }
    }
  }, options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS);
  return {
    get state() {
      return state;
    },
    get stopped() {
      return state === 'stopped' || state === 'error';
    },
    stop() {
      if (result) return Promise.resolve(result);
      if (failure) return Promise.reject(failure);
      if (stopping) return stopping;
      stopping = new Promise<AudioRecordingResult>((resolve, reject) => {
        resolveStop = resolve;
        rejectStop = reject;
      });
      try {
        if (recorder.state === 'recording') recorder.stop();
      } finally {
        // Permission is released immediately, even if onstop is delayed or never fires.
        stopTracks();
      }
      return stopping;
    },
    cancel() {
      if (state === 'stopped' || state === 'error') return;
      suppressResult = true;
      chunks.length = 0;
      stopTracks();
      rejectStop?.(new Error('audio_recording_cancelled'));
      rejectStop = null;
      if (timeout !== undefined) clearTimeoutFn(timeout);
      if (recorder.state === 'recording') recorder.stop();
      else {
        stopTracks();
        emit('stopped');
      }
    },
    onStateChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
